/*
 * COPYRIGHT © 2026 SANITÀNEXUS WHITE-LABEL. ALL RIGHTS RESERVED.
 * AUTHOR AND SOLE DEVELOPER: MARCO PANZETTI (C.F. PNZMRC89M14F251C)
 * Protected under Italian Copyright Law (Legge 633/1941) and International Treaties.
 * Unauthorized copying, reverse engineering, or distribution of this software architecture,
 * UI/UX layouts, local-first logic, or system prompt configurations is strictly prohibited.
 */
import React, { useEffect, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Progress } from '@/components/ui/progress';
import { Skeleton } from '@/components/ui/skeleton';
import { Badge } from '@/components/ui/badge';
import { TrendingUp, Users, Clock, Star, AlertCircle, Euro, Info } from 'lucide-react';
import { getBenchmark } from '@/lib/proToolkit';

const MetricRow = ({ icon: Icon, label, myValue, myUnit, peerValue, peerUnit, percentile, betterLower }) => {
  const pct = percentile;
  let tone = 'neutral';
  if (pct !== null && pct !== undefined) {
    if (pct >= 75) tone = 'good';
    else if (pct >= 40) tone = 'neutral';
    else tone = 'warning';
  }
  const toneColor = {
    good: 'text-emerald-600 dark:text-emerald-400',
    neutral: 'text-primary',
    warning: 'text-amber-600 dark:text-amber-400',
  }[tone];

  const percentileLabel = pct === null || pct === undefined
    ? 'Dati insufficienti'
    : pct >= 75
      ? `Top ${100 - pct}% dei colleghi`
      : pct >= 40
        ? `Nella media`
        : `Sotto la media`;

  return (
    <div className="rounded-2xl border border-border p-4 space-y-2" data-testid={`benchmark-row-${label}`}>
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Icon className="h-4 w-4 text-muted-foreground" />
          <span className="text-sm font-semibold">{label}</span>
        </div>
        {pct !== null && pct !== undefined && (
          <Badge variant="secondary" className={`text-xs font-bold ${toneColor}`}>
            Percentile {pct}°
          </Badge>
        )}
      </div>
      <div className="grid grid-cols-2 gap-3">
        <div>
          <div className="text-[10px] uppercase tracking-wider text-muted-foreground">Tu</div>
          <div className={`font-display font-bold text-2xl tabular-nums ${toneColor}`}>
            {myValue !== null && myValue !== undefined ? myValue : '—'}
            {myUnit && <span className="text-sm font-normal ml-1 text-muted-foreground">{myUnit}</span>}
          </div>
        </div>
        <div>
          <div className="text-[10px] uppercase tracking-wider text-muted-foreground">Mediana colleghi</div>
          <div className="font-display font-bold text-2xl tabular-nums text-muted-foreground">
            {peerValue !== null && peerValue !== undefined ? peerValue : '—'}
            {peerUnit && <span className="text-sm font-normal ml-1">{peerUnit}</span>}
          </div>
        </div>
      </div>
      {pct !== null && pct !== undefined && (
        <>
          <Progress value={pct} className="h-2" />
          <div className="text-xs text-muted-foreground">
            {percentileLabel}
            {betterLower && <span className="ml-1">(valori piu bassi = migliori)</span>}
          </div>
        </>
      )}
    </div>
  );
};

export const BenchmarkPanel = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const d = await getBenchmark();
        setData(d);
      } catch (e) {
        setError(e?.response?.data?.detail || 'Errore nel caricamento del benchmark');
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  if (loading) {
    return (
      <div className="space-y-3">
        {[...Array(4)].map((_, i) => (
          <Skeleton key={`bench-sk-${i}`} className="h-28 rounded-2xl" />
        ))}
      </div>
    );
  }

  if (error) {
    return (
      <Card>
        <CardContent className="p-6 text-center">
          <AlertCircle className="h-8 w-8 mx-auto text-destructive mb-2" />
          <p className="text-sm text-muted-foreground">{error}</p>
        </CardContent>
      </Card>
    );
  }

  if (!data) return null;

  const my = data.my_metrics || {};
  const peer = data.peer_medians || {};
  const p = data.percentiles || {};

  return (
    <div className="space-y-4" data-testid="benchmark-panel">
      <Card>
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="font-display">Come stai rispetto ai colleghi</CardTitle>
              <CardDescription>
                Ultimi {data.period_days} giorni {data.specialty ? `· ${data.specialty}` : ''}
              </CardDescription>
            </div>
            <Badge variant="secondary" className="flex items-center gap-1">
              <Users className="h-3 w-3" />
              {data.peer_sample_size} colleghi
            </Badge>
          </div>
        </CardHeader>
      </Card>

      <MetricRow
        icon={TrendingUp}
        label="Visite completate"
        myValue={my.completed}
        peerValue={peer.completed}
        percentile={p.completed}
      />
      <MetricRow
        icon={Clock}
        label="Tempo di risposta medio"
        myValue={my.avg_response_hours}
        myUnit="h"
        peerValue={peer.response_hours}
        peerUnit="h"
        percentile={p.response_hours}
        betterLower
      />
      <MetricRow
        icon={Star}
        label="Rating pazienti"
        myValue={my.avg_rating}
        myUnit="/5"
        peerValue={peer.rating}
        peerUnit="/5"
        percentile={p.rating}
      />
      <MetricRow
        icon={AlertCircle}
        label="Tasso di no-show"
        myValue={my.no_show_rate}
        myUnit="%"
        peerValue={peer.no_show_rate}
        peerUnit="%"
        percentile={p.no_show_rate}
        betterLower
      />
      <MetricRow
        icon={Euro}
        label="Fatturato del periodo"
        myValue={my.revenue_eur}
        myUnit="€"
        peerValue={peer.revenue_eur}
        peerUnit="€"
        percentile={p.revenue}
      />

      <div className="rounded-lg bg-muted/40 p-3 text-[11px] text-muted-foreground flex items-start gap-2">
        <Info className="h-3.5 w-3.5 mt-0.5 flex-shrink-0" />
        <span>{data.disclaimer}</span>
      </div>
    </div>
  );
};

export default BenchmarkPanel;
